import type {
  ArtifactSurface,
  Block,
  Citation,
  HealthResponse,
  ResolutionBlock,
  ResolutionStepState,
  ThreadSummary,
  TicketSummary,
  TicketDetail,
  ToolCall,
  Turn,
  WidgetView
} from "@shared/types";
import { authHeaders } from "./auth";

export type FetchHealthResult =
  | { kind: "ok"; health: HealthResponse }
  | { kind: "down"; reason: string };

export async function fetchHealth(): Promise<FetchHealthResult> {
  try {
    const res = await fetch("/healthz", { headers: { Accept: "application/json" } });
    if (!res.ok) return { kind: "down", reason: `HTTP ${res.status}` };
    return { kind: "ok", health: (await res.json()) as HealthResponse };
  } catch (err) {
    return { kind: "down", reason: err instanceof Error ? err.message : String(err) };
  }
}

export async function fetchSessions(): Promise<ThreadSummary[]> {
  try {
    const res = await fetch("/api/sessions", {
      headers: authHeaders({ Accept: "application/json" })
    });
    if (!res.ok) return [];
    const body = (await res.json()) as { sessions: ThreadSummary[] };
    return body.sessions;
  } catch {
    return [];
  }
}

export async function fetchSessionTurns(sessionId: string): Promise<Turn[]> {
  const res = await fetch(`/api/sessions/${sessionId}`, {
    headers: authHeaders({ Accept: "application/json" })
  });
  if (!res.ok) return [];
  const body = (await res.json()) as { turns: Turn[] };
  return body.turns;
}

export async function deleteSession(sessionId: string): Promise<void> {
  await fetch(`/api/sessions/${sessionId}`, { method: "DELETE", headers: authHeaders() });
}

/** A product-feedback signal: an unresolved reason (§2-4), a thumbs rating or
 * escalation (§2-1). The server enriches it with the originating question. */
export interface FeedbackPayload {
  sessionId: string | null;
  /** "unresolved" | "rating" | "escalate"; defaults to "unresolved" server-side. */
  kind?: string | undefined;
  /** Legacy single reason (unresolved/escalate flows still use it). */
  reason?: string | undefined;
  /** The reason(s) the user selected for a thumbs rating — multi-select (§2-1).
   * See openspec/answer-feedback-multi-reason.md. */
  reasons?: string[] | undefined;
  /** `null` retracts a prior rating (openspec/feedback-redesign-unified.md §3.2). */
  rating?: ("up" | "down" | null) | undefined;
  turnId?: string | undefined;
  note?: string | undefined;
  /** The rated turn's question + answer, so the server can persist the rating
   * under a stable content signature (live turn ids are ephemeral). */
  question?: string | undefined;
  answer?: string | undefined;
  /** Provider response id of the rated turn — the stable key that joins this
   * rating to its raw step record (openspec/rating-step-join.md). */
  responseId?: string | undefined;
  /** Optional screenshot attached on thumbs-down (openspec §3.4). */
  screenshot?: { data: string; mimeType: string } | undefined;
}

/**
 * Record a feedback signal (§2-4 unresolved reasons, §2-1 thumbs / escalate).
 * Best-effort — a failed POST must never block the conversation, so errors are
 * swallowed.
 */
export async function postFeedback(payload: FeedbackPayload): Promise<void> {
  try {
    await fetch("/api/feedback", {
      method: "POST",
      headers: authHeaders({ "Content-Type": "application/json" }),
      body: JSON.stringify(payload)
    });
  } catch {
    // best-effort telemetry; never surface to the user
  }
}

/** Product feedback about CycleCare itself (separate from answer ratings and
 * support tickets). Files an issue under the `cyclecare-feedback` label. */
export interface ProductFeedbackPayload {
  title: string;
  category: "bug" | "feature" | "usability" | "other";
  description: string;
  /** Optional screenshot (base64, no data: prefix). */
  screenshot?: { data: string; mimeType: string } | undefined;
  /** Capture context (openspec §5.1) — all optional. */
  conversationId?: string | undefined;
  responseId?: string | undefined;
  page?: string | undefined;
}

export async function postProductFeedback(
  payload: ProductFeedbackPayload
): Promise<{ ok: boolean; id?: number; number?: number }> {
  try {
    const res = await fetch("/api/product-feedback", {
      method: "POST",
      // Attach the Bearer token — with AUTH_ENABLED every /api/* call must carry
      // it or the edge hook rejects with 401 (#49). The feedback client predates
      // auth, so it was the one call still sending an unauthenticated request.
      headers: authHeaders({ "Content-Type": "application/json" }),
      body: JSON.stringify(payload)
    });
    const body = (await res.json().catch(() => ({}))) as {
      ok?: boolean;
      id?: number;
      number?: number;
    };
    return {
      ok: res.ok && body.ok === true,
      ...(typeof body.id === "number" ? { id: body.id } : {}),
      ...(typeof body.number === "number" ? { number: body.number } : {})
    };
  } catch {
    return { ok: false };
  }
}

/**
 * Fetch up to 3 context-specific "what went wrong" reasons for a thumbs-down
 * answer (§2-1). Best-effort and fast (turbo side-model); returns [] on any
 * failure so the caller falls back to a fixed reason set.
 */
/**
 * Fetch KB-grounded home-screen starter suggestions (§2-5). Best-effort and
 * fast (cached server-side per KB version); returns [] on any failure so the
 * caller keeps its static fallback list.
 */
export async function fetchSuggestions(): Promise<string[]> {
  try {
    const res = await fetch("/api/suggestions", {
      headers: authHeaders({ Accept: "application/json" })
    });
    if (!res.ok) return [];
    const data = (await res.json()) as { suggestions?: unknown };
    return Array.isArray(data.suggestions)
      ? data.suggestions.filter((s): s is string => typeof s === "string")
      : [];
  } catch {
    return [];
  }
}

export async function fetchAnswerReasons(
  question: string,
  answer: string,
  rating: "up" | "down"
): Promise<string[]> {
  try {
    const res = await fetch("/api/feedback/reasons", {
      method: "POST",
      headers: authHeaders({ "Content-Type": "application/json" }),
      body: JSON.stringify({ question, answer, rating })
    });
    if (!res.ok) return [];
    const data = (await res.json()) as { reasons?: unknown };
    return Array.isArray(data.reasons)
      ? data.reasons.filter((r): r is string => typeof r === "string")
      : [];
  } catch {
    return [];
  }
}

/**
 * Persist a resolution-panel step-state change (Feature 2). Returns the updated
 * block on success so the caller can apply the authoritative state, or null on
 * any failure (the panel keeps its current state and re-enables its buttons).
 */
export async function mutateResolutionStep(
  sessionId: string,
  blockId: string,
  stepId: string,
  state: ResolutionStepState
): Promise<ResolutionBlock | null> {
  try {
    const res = await fetch(
      `/api/blocks/${encodeURIComponent(sessionId)}/${encodeURIComponent(blockId)}/step`,
      {
        method: "POST",
        headers: authHeaders({ "Content-Type": "application/json" }),
        body: JSON.stringify({ stepId, state })
      }
    );
    if (!res.ok) return null;
    const data = (await res.json()) as { ok?: boolean; block?: ResolutionBlock };
    return data.ok === true && data.block !== undefined ? data.block : null;
  } catch {
    return null;
  }
}

/**
 * Persist a suggested-next-step dismissal (openspec/suggested-next-steps.md).
 * Fire-and-forget from the chip's ✕: the chip is hidden locally regardless;
 * the durable row keeps it hidden on reload and stops the generator from
 * re-proposing the same label this session.
 */
export async function dismissNextStep(
  sessionId: string,
  suggestionId: string,
  label: string
): Promise<boolean> {
  try {
    const res = await fetch(`/api/blocks/${encodeURIComponent(sessionId)}/next-steps/dismiss`, {
      method: "POST",
      headers: authHeaders({ "Content-Type": "application/json" }),
      body: JSON.stringify({ suggestionId, label })
    });
    return res.ok;
  } catch {
    return false;
  }
}

export interface TicketsResult {
  configured: boolean;
  enterprise: string;
  tickets: TicketSummary[];
}

/** List enterprise tickets, optionally filtered to a user's email (我的工单). */
export async function fetchTickets(email?: string): Promise<TicketsResult> {
  const qs = email !== undefined && email.length > 0 ? `?email=${encodeURIComponent(email)}` : "";
  try {
    const res = await fetch(`/api/tickets${qs}`, {
      headers: authHeaders({ Accept: "application/json" })
    });
    if (!res.ok) return { configured: false, enterprise: "", tickets: [] };
    return (await res.json()) as TicketsResult;
  } catch {
    return { configured: false, enterprise: "", tickets: [] };
  }
}

/**
 * Fetch one ticket's full detail (description + follow-up comments). Ownership
 * for end_users is enforced server-side from the auth token (#94), so no email
 * is needed here.
 */
export async function fetchTicketDetail(num: number): Promise<TicketDetail | null> {
  try {
    const res = await fetch(`/api/tickets/${num}`, {
      headers: authHeaders({ Accept: "application/json" })
    });
    if (!res.ok) return null;
    return (await res.json()) as TicketDetail;
  } catch {
    return null;
  }
}

/** Request human handoff: creates a trackable 转人工 ticket + notifies Feishu. */
export async function postHandoff(
  sessionId: string | null
): Promise<{ ok: boolean; number?: number; text?: string; reason?: string }> {
  try {
    const res = await fetch("/api/handoff", {
      method: "POST",
      headers: authHeaders({ "Content-Type": "application/json" }),
      body: JSON.stringify(sessionId !== null ? { sessionId } : {})
    });
    const body = (await res.json().catch(() => ({}))) as {
      ok?: boolean;
      number?: number;
      text?: string;
      reason?: string;
    };
    const ok = res.ok && body.ok === true;
    return {
      ok,
      ...(body.number !== undefined ? { number: body.number } : {}),
      // Canonical confirmation text composed + persisted server-side (§20.7.1)
      // so the live turn matches what a later reload reconstructs.
      ...(body.text !== undefined && body.text.length > 0 ? { text: body.text } : {}),
      ...(body.reason !== undefined && body.reason.length > 0 ? { reason: body.reason } : {})
    };
  } catch (err) {
    return { ok: false, reason: err instanceof Error ? err.message : String(err) };
  }
}

/**
 * Streams a single chat turn via SSE-over-fetch. Guarantees exactly
 * one of onDone or onError is invoked. If the underlying stream
 * closes before a terminal event arrives, surfaces an
 * `stream_closed_without_terminal` error so the UI can recover.
 */
export interface ChatStreamCallbacks {
  onStart: (payload: {
    sessionId: string;
    userTurn: Turn;
    assistantTurnId: string;
    /** M2 pill: the serving assistant (present only under FEATURE_AGENT_PROFILE). */
    assistant?: { profileId: string; name: string };
  }) => void;
  onDelta: (text: string, assistantTurnId: string) => void;
  onTool: (tool: ToolCall, assistantTurnId: string) => void;
  onCitations: (citations: Citation[], assistantTurnId: string) => void;
  onWidget: (widget: WidgetView, assistantTurnId: string) => void;
  onBlock: (block: Block, assistantTurnId: string) => void;
  /** LOOM_LIVE: the model marked this answer as runnable in the user's 织灵. */
  onRunnable: (assistantTurnId: string) => void;
  /** M2: the serving assistant switched mid-turn (transfer takeover). */
  onAssistant?: (assistant: { profileId: string; name: string }) => void;
  onDone: (payload: {
    latencyMs: number;
    fullText: string;
    assistantTurnId: string;
    responseId?: string;
  }) => void;
  onError: (message: string, code?: string) => void;
}

export interface OutgoingImage {
  data: string;
  mimeType: string;
}

/** A model the chat selector can offer (GET /api/models). */
export interface ChatModel {
  id: string;
  name: string;
  available: boolean;
}

/** An assistant the caller may @-mention (agent-profile M2, T4-filtered server-side). */
export interface MentionableAssistant {
  id: string;
  name: string;
  tagline?: string;
  isDefault: boolean;
}

/** @-popover feed; [] when the flag is off (route absent) or tenant unbound. */
export async function getMentionableAssistants(): Promise<MentionableAssistant[]> {
  try {
    const res = await fetch("/api/profiles/mentionable", {
      headers: authHeaders({ Accept: "application/json" })
    });
    if (!res.ok) return [];
    const body = (await res.json()) as { assistants?: MentionableAssistant[] };
    return Array.isArray(body.assistants) ? body.assistants : [];
  } catch {
    return [];
  }
}

/** M2 capability card — fully server-rendered strings (§09 guarantee). */
export interface AssistantCardData {
  name: string;
  tagline?: string;
  sections: { heading: string; text: string }[];
  startersHeading: string;
  starters: string[];
  footer: string;
}

export async function getAssistantCard(profileId: string): Promise<AssistantCardData | null> {
  try {
    const res = await fetch(`/api/profiles/${encodeURIComponent(profileId)}/card`, {
      headers: authHeaders({ Accept: "application/json" })
    });
    if (!res.ok) return null;
    return (await res.json()) as AssistantCardData;
  } catch {
    return null;
  }
}

export async function getModels(): Promise<ChatModel[]> {
  try {
    const res = await fetch("/api/models", {
      headers: authHeaders({ Accept: "application/json" })
    });
    if (!res.ok) return [];
    const body = (await res.json()) as { models?: ChatModel[] };
    return Array.isArray(body.models) ? body.models : [];
  } catch {
    return [];
  }
}

export async function streamChat(
  sessionId: string | null,
  message: string,
  cb: ChatStreamCallbacks,
  signal: AbortSignal,
  images?: OutgoingImage[],
  modelId?: string,
  /** In-doc ask (FEATURE_MANUAL_ASK): pin retrieval to this manual's KB source. */
  manualSource?: string,
  /** M2 @-mention: the assistant the user explicitly picked for this turn. */
  mention?: string,
  /** Artifact 重新生成 (task #7 · 7b MVP): append a new version to this artifact
   *  id instead of creating a new object. */
  regenerateArtifactId?: string
): Promise<void> {
  const body: Record<string, unknown> = { message };
  if (sessionId !== null) body.sessionId = sessionId;
  if (images !== undefined && images.length > 0) body.images = images;
  if (modelId !== undefined && modelId.length > 0) body.modelId = modelId;
  if (manualSource !== undefined) body.manualSource = manualSource;
  if (mention !== undefined && mention.length > 0) body.mention = mention;
  if (regenerateArtifactId !== undefined && regenerateArtifactId.length > 0) {
    body.regenerateArtifactId = regenerateArtifactId;
  }

  let assistantTurnId = "";
  let terminalSeen = false;

  let res: Response;
  try {
    res = await fetch("/api/chat", {
      method: "POST",
      headers: authHeaders({ "Content-Type": "application/json", Accept: "text/event-stream" }),
      body: JSON.stringify(body),
      signal
    });
  } catch (err) {
    cb.onError(err instanceof Error ? err.message : String(err), "FETCH_ERROR");
    return;
  }
  if (!res.ok) {
    let bodyText = "";
    try {
      bodyText = await res.text();
    } catch {
      // ignore
    }
    cb.onError(`HTTP ${res.status}: ${bodyText.slice(0, 200)}`, `HTTP_${res.status}`);
    return;
  }
  if (res.body === null) {
    cb.onError("response body is null", "EMPTY_BODY");
    return;
  }

  const reader = res.body.pipeThrough(new TextDecoderStream()).getReader();
  let buf = "";
  try {
    for (;;) {
      const { value, done } = await reader.read();
      if (done) break;
      buf += value;
      let sepIdx;
      while ((sepIdx = buf.indexOf("\n\n")) !== -1) {
        const record = buf.slice(0, sepIdx);
        buf = buf.slice(sepIdx + 2);
        const handled = dispatchSseRecord(record, {
          ...cb,
          onStart: (p) => {
            assistantTurnId = p.assistantTurnId;
            cb.onStart(p);
          },
          onDelta: (text) => cb.onDelta(text, assistantTurnId),
          onTool: (tool) => cb.onTool(tool, assistantTurnId),
          onCitations: (citations) => cb.onCitations(citations, assistantTurnId),
          onWidget: (widget) => cb.onWidget(widget, assistantTurnId),
          onBlock: (block) => cb.onBlock(block, assistantTurnId),
          onRunnable: () => cb.onRunnable(assistantTurnId),
          onDone: (p) => {
            terminalSeen = true;
            cb.onDone({ ...p, assistantTurnId });
          },
          onError: (m, c) => {
            terminalSeen = true;
            cb.onError(m, c);
          }
        });
        if (!handled) {
          console.warn("dropped malformed SSE record", record.slice(0, 200));
        }
      }
    }
    if (!terminalSeen) {
      cb.onError(
        "stream closed without terminal event (done/error)",
        "STREAM_CLOSED_WITHOUT_TERMINAL"
      );
    }
  } catch (err) {
    if (signal.aborted) return;
    cb.onError(err instanceof Error ? err.message : String(err), "STREAM_THROW");
  }
}

function dispatchSseRecord(record: string, cb: ChatStreamCallbacks): boolean {
  let event = "message";
  const dataLines: string[] = [];
  for (const line of record.split("\n")) {
    if (line.startsWith(":")) continue; // comment / heartbeat
    if (line.startsWith("event:")) {
      event = line.slice(6).trim();
    } else if (line.startsWith("data:")) {
      dataLines.push(line.slice(5).trimStart());
    }
  }
  if (dataLines.length === 0) return true; // comment-only or empty record
  let payload: unknown;
  try {
    payload = JSON.parse(dataLines.join("\n"));
  } catch {
    return false;
  }
  if (event === "start") {
    cb.onStart(
      payload as {
        sessionId: string;
        userTurn: Turn;
        assistantTurnId: string;
        assistant?: { profileId: string; name: string };
      }
    );
  } else if (event === "delta") {
    cb.onDelta((payload as { text: string }).text, "");
  } else if (event === "tool") {
    cb.onTool(payload as ToolCall, "");
  } else if (event === "citations") {
    cb.onCitations((payload as { citations: Citation[] }).citations, "");
  } else if (event === "widget") {
    cb.onWidget(payload as WidgetView, "");
  } else if (event === "block") {
    cb.onBlock(payload as Block, "");
  } else if (event === "artifact") {
    // FEATURE_ARTIFACTS (task #7 · Phase 7a): the host persisted a generated
    // deliverable and sends its id/title. Reuse the block channel — build the
    // ArtifactBlock here (id matches the reload overlay's `artifact_${id}`) so an
    // in-chat card lands in the turn exactly like a natively streamed block.
    const a = payload as { id: string; title: string; surface: ArtifactSurface };
    cb.onBlock(
      { id: `artifact_${a.id}`, type: "artifact", artifactId: a.id, title: a.title, surface: a.surface },
      ""
    );
  } else if (event === "runnable") {
    cb.onRunnable("");
  } else if (event === "assistant") {
    cb.onAssistant?.(payload as { profileId: string; name: string });
  } else if (event === "done") {
    cb.onDone({
      ...(payload as { latencyMs: number; fullText: string; responseId?: string }),
      assistantTurnId: ""
    });
  } else if (event === "error") {
    const p = payload as { message: string; code?: string };
    cb.onError(p.message, p.code);
  }
  return true;
}
